/**
 * Schmaler HTTP-Client für die VLZHUB-API.
 *
 * Läuft ein Access-Token ab, wird genau einmal automatisch erneuert und die
 * ursprüngliche Anfrage wiederholt. Parallele Anfragen teilen sich dabei denselben
 * Erneuerungsvorgang, damit nicht mehrere Refresh-Aufrufe gleichzeitig starten.
 */

const SCHLUESSEL_ACCESS = 'vlzhub.accessToken'
const SCHLUESSEL_REFRESH = 'vlzhub.refreshToken'

export interface BenutzerAntwort {
  id: number
  email: string
  anzeigename: string
  rollen: string[]
  mandantId: number
  mandantName: string | null
  mandantKuerzel: string | null
}

export interface TokenAntwort {
  accessToken: string
  refreshToken: string
  gueltigSekunden: number
  benutzer: BenutzerAntwort
}

export class ApiFehler extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

export const tokenSpeicher = {
  access: () => localStorage.getItem(SCHLUESSEL_ACCESS),
  refresh: () => localStorage.getItem(SCHLUESSEL_REFRESH),
  setzen(access: string, refresh: string) {
    localStorage.setItem(SCHLUESSEL_ACCESS, access)
    localStorage.setItem(SCHLUESSEL_REFRESH, refresh)
  },
  leeren() {
    localStorage.removeItem(SCHLUESSEL_ACCESS)
    localStorage.removeItem(SCHLUESSEL_REFRESH)
  },
}

let laufendeErneuerung: Promise<boolean> | null = null

async function erneuere(): Promise<boolean> {
  const refreshToken = tokenSpeicher.refresh()
  if (!refreshToken) return false

  // Mehrere gleichzeitig scheiternde Anfragen sollen nur eine Erneuerung auslösen.
  if (!laufendeErneuerung) {
    laufendeErneuerung = (async () => {
      try {
        const antwort = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
        if (!antwort.ok) {
          tokenSpeicher.leeren()
          return false
        }
        const daten: TokenAntwort = await antwort.json()
        tokenSpeicher.setzen(daten.accessToken, daten.refreshToken)
        return true
      } catch {
        return false
      } finally {
        laufendeErneuerung = null
      }
    })()
  }
  return laufendeErneuerung
}

async function fehlertext(antwort: Response): Promise<string> {
  try {
    const koerper = await antwort.json()
    return koerper.message || koerper.error || `Fehler ${antwort.status}`
  } catch {
    return `Fehler ${antwort.status}`
  }
}

/**
 * Holt eine binäre Antwort mit Token — und erneuert es einmal, wenn es abgelaufen ist.
 *
 * Ein schlichtes `<a href>` genügt hier nicht: Das Token steht im localStorage, nicht in
 * einem Cookie, und der Browser schickt bei einem gewöhnlichen Verweis keinen
 * Authorization-Kopf mit.
 */
async function holeAntwort(pfad: string, methode: string): Promise<Response> {
  const kopfzeilen = new Headers()
  const token = tokenSpeicher.access()
  if (token) kopfzeilen.set('Authorization', `Bearer ${token}`)

  let antwort = await fetch(pfad, { method: methode, headers: kopfzeilen })
  if (antwort.status === 401 && (await erneuere())) {
    const neu = new Headers()
    const frisch = tokenSpeicher.access()
    if (frisch) neu.set('Authorization', `Bearer ${frisch}`)
    antwort = await fetch(pfad, { method: methode, headers: neu })
  }
  if (!antwort.ok) {
    throw new ApiFehler(antwort.status, await fehlertext(antwort))
  }
  return antwort
}

/** Der Dateiname aus `Content-Disposition`, RFC-5987-kodiert oder schlicht. */
function nameAusKopfzeile(antwort: Response): string | null {
  const kopf = antwort.headers.get('Content-Disposition')
  if (!kopf) return null
  const kodiert = /filename\*=UTF-8''([^;]+)/i.exec(kopf)
  if (kodiert) {
    try {
      return decodeURIComponent(kodiert[1])
    } catch {
      return kodiert[1]
    }
  }
  const schlicht = /filename="?([^";]+)"?/i.exec(kopf)
  return schlicht ? schlicht[1] : null
}

async function anfrage<T>(pfad: string, optionen: RequestInit = {}, zweiterVersuch = false): Promise<T> {
  const kopfzeilen = new Headers(optionen.headers)
  const token = tokenSpeicher.access()
  if (token) kopfzeilen.set('Authorization', `Bearer ${token}`)
  if (optionen.body && !(optionen.body instanceof FormData)) {
    kopfzeilen.set('Content-Type', 'application/json')
  }

  const antwort = await fetch(pfad, { ...optionen, headers: kopfzeilen })

  if (antwort.status === 401 && !zweiterVersuch && tokenSpeicher.refresh()) {
    if (await erneuere()) {
      return anfrage<T>(pfad, optionen, true)
    }
    tokenSpeicher.leeren()
    window.dispatchEvent(new Event('vlzhub:abgemeldet'))
    throw new ApiFehler(401, 'Sitzung abgelaufen')
  }

  if (!antwort.ok) {
    throw new ApiFehler(antwort.status, await fehlertext(antwort))
  }

  if (antwort.status === 204) return undefined as T
  return (await antwort.json()) as T
}

export const api = {
  get: <T>(pfad: string) => anfrage<T>(pfad),
  post: <T>(pfad: string, koerper?: unknown) =>
    anfrage<T>(pfad, { method: 'POST', body: koerper ? JSON.stringify(koerper) : undefined }),
  put: <T>(pfad: string, koerper: unknown) =>
    anfrage<T>(pfad, { method: 'PUT', body: JSON.stringify(koerper) }),
  loeschen: <T>(pfad: string) => anfrage<T>(pfad, { method: 'DELETE' }),
  hochladen: <T>(pfad: string, datei: File) => {
    const formular = new FormData()
    formular.append('datei', datei)
    return anfrage<T>(pfad, { method: 'POST', body: formular })
  },
  /**
   * Holt eine Datei und übergibt sie dem Browser zum Speichern.
   *
   * Ein schlichtes `<a href="/api/…">` genügt hier nicht: Das Token steht im
   * localStorage, nicht in einem Cookie, und der Browser schickt bei einem
   * gewöhnlichen Verweis keinen Authorization-Kopf mit — der Server antwortet dann
   * mit 401. Der Umweg über fetch und einen Objekt-URL ist der Preis für die
   * zustandslose Anmeldung.
   */
  async herunterladen(pfad: string, dateiname?: string, methode: string = 'GET'): Promise<void> {
    const antwort = await holeAntwort(pfad, methode)
    const blob = await antwort.blob()
    const adresse = URL.createObjectURL(blob)
    const verweis = document.createElement('a')
    verweis.href = adresse
    verweis.download = dateiname ?? nameAusKopfzeile(antwort) ?? 'datei'
    document.body.appendChild(verweis)
    verweis.click()
    verweis.remove()
    // Erst nach dem Klick freigeben, sonst bricht der Download in Firefox ab.
    setTimeout(() => URL.revokeObjectURL(adresse), 10_000)
  },
  /**
   * Holt eine Datei als Blob, ohne sie zu speichern — für Inhalte, die angezeigt statt
   * abgelegt werden, etwa ein QR-Code am Bildschirm.
   */
  async holeBlob(pfad: string, methode: string = 'GET'): Promise<Blob> {
    return (await holeAntwort(pfad, methode)).blob()
  },
  /**
   * Wie holeBlob, liefert aber die Antwort mit. Gebraucht beim Kopplungs-QR: Das Bild
   * allein genuegt nicht, der Code steht als Kopfzeile daneben und wird zum Abtippen
   * angezeigt.
   */
  async holeBlobMitKopf(
    pfad: string,
    methode: string = 'GET',
  ): Promise<{ blob: Blob; kopf: Headers }> {
    const antwort = await holeAntwort(pfad, methode)
    return { blob: await antwort.blob(), kopf: antwort.headers }
  },
  async anmelden(email: string, passwort: string): Promise<TokenAntwort> {
    const antwort = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, passwort }),
    })
    if (!antwort.ok) {
      throw new ApiFehler(antwort.status, await fehlertext(antwort))
    }
    const daten: TokenAntwort = await antwort.json()
    tokenSpeicher.setzen(daten.accessToken, daten.refreshToken)
    return daten
  },
}

/** Minuten ab Betriebstagbeginn als HH:MM — auch jenseits von 24 Stunden. */
export function alsUhrzeit(minuten: number | null | undefined): string {
  if (minuten === null || minuten === undefined) return '–'
  const stunden = Math.floor(minuten / 60)
  const rest = minuten % 60
  return `${String(stunden).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/** Minutenzahl als Dauer "8:16 h". */
export function alsDauer(minuten: number | null | undefined): string {
  if (minuten === null || minuten === undefined) return '–'
  return `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, '0')} h`
}

export function alsDatum(iso: string | null | undefined): string {
  if (!iso) return '–'
  const [jahr, monat, tag] = iso.split('-')
  return `${tag}.${monat}.${jahr}`
}

const WOCHENTAG_KURZ: Record<string, string> = {
  MONDAY: 'Mo',
  TUESDAY: 'Di',
  WEDNESDAY: 'Mi',
  THURSDAY: 'Do',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
  SUNDAY: 'So',
}

export function wochentagKurz(tag: string): string {
  return WOCHENTAG_KURZ[tag] ?? tag
}
